<style scoped>
  @import './otp-authentication.scss';
</style>

<script>
import { mapGetters } from 'vuex'
import Continue from '../continue/continue.vue'
import { CONSTANT, SDK_ENUMS } from '@/utils/constant.js'
import { EventBus } from '@/event-bus.js'
import { store } from '@/store/store'
import orchestration from '@/orchestration'
import sharedService from '@/utils/shared-service'
import { BPopover } from 'bootstrap-vue'

export default {
  name: 'OtpAuthentication',

  components: {
    Continue,
    BPopover
  },

  inheritAttrs: true,

  props: {
    value: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      otp: [],
      otpLengthErr: false,
      error: '',
      successMsg: '',
      valueParts: _fill([], '', CONSTANT.CODE_LENGTH),
      limitExceedError: false
    }
  },

  computed: {
    ...mapGetters(['validationData', 'identityFoundAt', 'selectedCheckoutExperience', 'storeSuccessMsg', 'enableMPT']),
    rememberMe: {
      get () {
        return this.$store.getters.rememberMe
      },
      set (value) {
        if (window.opener && window.opener.childSrcWindow) {
          window.opener.postMessage({ rememberMe: value })
        }
        this.$store.dispatch('updateRememberMeConsent', value)
      }
    }
  },

  watch: {
    valueParts () {
      this.emitOtpInput()
    }
  },

  created () {
    this.$store.commit('lightBoxloaderOn', false)
    this.successMsg = this.storeSuccessMsg
    this.resetOtpBoxes()
  },

  mounted () {
    EventBus.$on('handleError', (event) => {
      this.$store.commit('lightBoxloaderOn', false)
      this.error = event
      setTimeout(() => {
        if (this.error === 'ACCT_INACCESSIBLE' || this.error === 'RETRIES_EXCEEDED') {
          if (this.enableMPT) {
            this.$store.dispatch('updateRememberMeConsent', false)
            sharedService.srciScreenChange(CONSTANT.INLINE_SCREEN.REGISTER_SCREEN, this.$router)
          } else {
            this.$store.commit('srciActionCode', SDK_ENUMS.SrciActionCodes.authFailed)
            sharedService.srciScreenChange(CONSTANT.INLINE_SCREEN.GUEST_CHECKOUT_SCREEN, this.$router)
          }
        }
      }, 2000)
      this.resetOtpBoxes()
    })
    EventBus.$on('handleSuccess', (event) => {
      this.$store.commit('lightBoxloaderOn', false)
      this.successMsg = event
      setTimeout(() => {
        this.successMsg = false
      }, 2000)
      this.resetOtpBoxes()
    })
  },

  methods: {

    /**
     * join all values of otp boxes and store in otp
     */
    emitOtpInput () {
      this.otp = this.valueParts.join('').slice(0, CONSTANT.CODE_LENGTH)
    },

    /**
     * Resetting input boxes of OTP to empty
     */
    resetOtpBoxes () {
      this.otp = ''
      this.valueParts = _fill([], '', CONSTANT.CODE_LENGTH)
      this.$store.commit('lightBoxloaderOn', false)
      setTimeout(() => {
        this.successMsg = ''
        this.errorReason = ''
      }, 4000)
    },

    /**
     * focusing input box by index number
     * @param index
     */
    focus (index) {
      const inp = this.$refs.wrap.children[index]
      if (inp) {
        inp.focus()
      }
    },

    /* eslint complexity: ["error", 14] */
    /**
     * Handle event of keydown
     * @param event
     * @param index
     */
    handleKeyDown (event, index) {
      const key = _sanitizeEventKey(event.key)
      if (event.key === 'Enter') {
        this.$emit('enter')
        event.stopPropagation()
      }
      if (!key) {
        return false
      } else if (key === 'Backspace') {
        if (this.valueParts[index]) {
          return (this.valueParts[index] = '')
        }
        this.focus(index - 1)
      } else if (
        !event.shiftKey &&
        (key === 'ArrowRight' || key === 'Right')
      ) {
        this.focus(index + 1)
      } else if (
        !event.shiftKey &&
        (key === 'ArrowLeft' || key === 'Left')
      ) {
        this.focus(index - 1)
      } else if (key.length === 1 && this.valueParts[index]) {
        this.valueParts[index] = key
        this.$forceUpdate()
        this.focus(index + 1)
        this.emitOtpInput()
      }
    },

    /**
     * handle event of input
     * @param event
     * @param index
     */
    handleInput (event, index) {
      const value = this.valueParts[index]
      if (value) {
        if (value.length > 1) {
          this.valueParts[index] = value[value.length - 1]
        }
        this.focus(index + 1)
      }
    },

    /**
     * This method validate the otp
     * @param otp
     * @returns {Promise<void>}
     */
    async completeIdValidation () {
      this.$store.commit('lightBoxloaderOn', true)
      orchestration.callMethod({
        methodName: CONSTANT.TRACE.METHOD.COMPLETE_ID_VALIDATION,
        methodParameters: this.otp,
        enabledSdks: [this.identityFoundAt]
      }).then(completeIdResponse => {
        if (completeIdResponse.idToken) {
          if (completeIdResponse.recognitionToken) {
            let recognitionToken = store.getters.recognitionToken
            let environment = store.getters.environment
            recognitionToken[environment] = completeIdResponse.recognitionToken
            if (window.opener && window.opener.childSrcWindow) {
              window.opener.postMessage({ recognitionToken: recognitionToken })
            }
            store.commit('setRecognitionToken', recognitionToken)
          }
          store.commit('consumerAuthorization', [completeIdResponse.idToken])
          sharedService.srciScreenChange(CONSTANT.INLINE_SCREEN.PAYMENT_SCREEN, this.$router)
        } else {
          EventBus.$emit('handleError', completeIdResponse.reason || completeIdResponse.error.reason)
        }
      }).catch(error => {
        EventBus.$emit('handleError', error)
      }).finally(() => {
        this.$store.commit('lightBoxloaderOn', false)
      })
    },

    /**
     * recall initiateIdentityValidation on click of resend code
     */
    async resendOtp () {
      sharedService.srciScreenChange(CONSTANT.INLINE_SCREEN.OTP_METHOD_SCREEN, this.$router)
    },

    /**
     * This method submits the otp
     * @param otp
     */
    submitForm () {
      this.emitOtpInput()
      this.otpLengthErr = this.otp.length < CONSTANT.CODE_LENGTH
      if (this.otpLengthErr) {
        return false
      } else {
        this.completeIdValidation()
      }
    },

    redirect () {

    }
  }
}

function _fill (arr, val, l) {
  arr.length = l
  arr.fill(val, 0, l)
  return arr
}

function _sanitizeEventKey (key) {
  return key === 'Unidentified' ? undefined : key
}
</script>

<template>
  <div>
    <div
      v-if="error"
      class="alert alert-danger"
    >
      {{ $t('errorMessages.' +error) }}
    </div>
    <div
      v-if="successMsg"
      class="alert alert-success"
    >
      {{ $t ('successMessages.' + successMsg) }}
    </div>
    <h4 class="text-center mt-4 font-weight-bold">
      {{ $t ('label.verifyOtpText') }}
    </h4>
    <br>
    <h5 class="text-center mt-2 mb-3">
      {{ $t ('label.enterOtp') }} <span class="text-capitalize">{{ identityFoundAt.toLowerCase() }}</span>
      {{ $t ('label.sentTo') }}
      <div>{{ validationData.maskedValidationChannel }}</div>
    </h5>
    <div
      ref="wrap"
      class="align-center mt-4"
    >
      <template v-for="(char, index) in valueParts">
        <input
          :key="index"
          v-model="valueParts[index]"
          :autofocus="index === 0"
          v-bind="$attrs"
          type="text"
          onkeypress="if(this.value.length===1) return false;"
          class="otp-input form-control d-inline"
          minlength="1"
          maxlength="1"
          inputmode="numeric"
          @keydown="handleKeyDown($event, index)"
          @input="handleInput($event, index)"
        >
      </template>
    </div>
    <p
      v-if="otpLengthErr"
      id="otpHelpBlock"
      class="form-text text-center text-danger"
    >
      {{ $t ('errorMessages.invalidOtp') }}
    </p>
    <p
      v-if="limitExceedError"
      id="limitExceedError"
      class="form-text text-center text-danger"
    >
      {{ $t ('errorMessages.RETRIES_EXCEEDED') }}
    </p>
    <p class="text-center mt-3">
      <span
        id="continue-another-way"
        class="btn-footer"
        @click="resendOtp"
      >
        {{ $t ('label.continueAnotherWay') }}
      </span>
    </p>

    <div class="form-check rm-checkbox text-center">
      <input
        id="remember-me"
        v-model="rememberMe"
        class="form-check-input rm-input"
        type="checkbox">
      <label
        class="form-check-label rm-label"
        for="remember-me-cookie">
        <strong> {{ $t ('label.rememberMeBrowser') }}</strong>
      </label>
      <i
        id="popover-target"
        class="fa fa-info-circle info-pad"
        aria-hidden="true"/>
      <b-popover
        target="popover-target"
        triggers="hover"
        placement="bottom">
        If you’re remembered,you won’t need <br>to enter a
        code next time to securely <br> access your saved cards.
        <br><br>Not recommended for public or shared <br> devices
        because this uses cookies.
      </b-popover>
    </div>
    <continue
      @submit="submitForm()"
    />
  </div>
</template>
